import React from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { selectCurrentUserPreferences } from "modules/userPreferences/redux/selectors";
// eslint-disable-next-line you-dont-need-lodash-underscore/values
import { values } from "lodash";
import useEventTracking from "modules/eventTracking/hooks/useEventTracking";
import { selectIsEventOwner } from "modules/event/selectors";
import { selectIsMobile } from "modules/deviceInfo";
import { isTypeOfIMapTemplate } from "modules/event/template";
import { setIsInHostOnboarding } from "./theaterSlice";
import { selectTheaterTemplate } from "./selectors";

interface ITheaterViewContextProviderProps {
  children?: React.ReactNode;
}

export const HostOnboardingEffects = React.memo(() => {
  const dispatch = useAppDispatch();
  const isMobileView = useSelector(selectIsMobile);
  const preference = useSelector(selectCurrentUserPreferences);
  // Show host onboarding only for the admin of current event
  const isEventOwner = useSelector(selectIsEventOwner);
  const template = useSelector(selectTheaterTemplate);

  // Checks if remo floor plan and second room is in right side of first room
  const isHostOnboardingAllowed = React.useCallback(() => {
    if (isTypeOfIMapTemplate(template) && !template.isPrivate) {
      const [firstRoom, secondRoom] = values(template.rooms).sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      );

      if (firstRoom.x < secondRoom.x) {
        return true;
      }
    }

    return false;
  }, [template]);

  React.useEffect(() => {
    if (!isMobileView && preference) {
      dispatch(
        setIsInHostOnboarding(
          Boolean(
            isHostOnboardingAllowed() &&
              preference.isInHostOnboarding &&
              isEventOwner,
          ),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preference, isEventOwner, template, isHostOnboardingAllowed]);

  return null;
});

const TheaterViewContextProvider = (
  props: ITheaterViewContextProviderProps,
) => {
  const { children } = props;
  const { trackEventAttend } = useEventTracking();

  React.useEffect(() => {
    trackEventAttend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {children}
      <HostOnboardingEffects />
    </>
  );
};

export default TheaterViewContextProvider;
